import { XMarkIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { VideoSideBar } from "@components/VideoSideBar/VideoSideBar";
import { VideoComments } from "@pages/VideoSessions/components/Comments/VideoComments";
import { HeaderContent } from "@pages/VideoSessions/components/HeaderContent";
import { VideoMoments } from "@pages/VideoSessions/components/Moments/VideoMoments";
import { VideoSummary } from "@pages/VideoSessions/components/Summary/VideoSummary";
import { VideoTranscript } from "@pages/VideoSessions/components/Transcript/VideoTranscript";
import { displayDate } from "@pages/VideoSessions/components/Utils/formatDate";
import { VideoContainer } from "@pages/VideoSessions/components/VideoContainer";
import { VideoSessionMode, VideoSessionTab, useVideoSessionData } from "@providers/VideoSessionProvider";

interface VideoPlayerReadyEventI {
  duration: number;
}
declare global {
  interface WindowEventMap {
    videoPlayerReady: CustomEvent<VideoPlayerReadyEventI>;
  }
}

export interface VideoSessionDetailsPinnedNote {
  authorFirstName: string;
  authorLastName: string;
  content: string;
  createdDate: string;
}

interface VideoSessionDetailsHeaderOverride {
  learnerFullName: string;
  caption: string;
}

interface VideoSessionDetailsProps {
  /**
   * Routed (default): renders the full session experience (all tabs,
   * session-driven header, close = navigate back to /video-sessions).
   * Overlay: for inline mounts (e.g. expert chat) — allows the close
   * button to be intercepted, the tab list to be trimmed, and a pinned
   * note to be rendered at the top of the comments column.
   */
  mode?: VideoSessionMode;
  availableTabs?: VideoSessionTab[];
  pinnedNote?: VideoSessionDetailsPinnedNote;
  headerOverride?: VideoSessionDetailsHeaderOverride;
  onClose?: () => void;
  /** Seek the player to this time (seconds) once it's ready. Only used
   *  when opening from an echo-bubble click. */
  initialSeekTime?: number;
  /** Highlight this comment ID in the thread once the overlay opens. */
  initialHighlightCommentId?: string;
}

export const VideoSessionDetails: React.FC<VideoSessionDetailsProps> = ({
  mode = VideoSessionMode.Routed,
  availableTabs,
  pinnedNote,
  headerOverride,
  onClose,
  initialSeekTime,
  initialHighlightCommentId,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const startTime = parseFloat(searchParams.get("startTime") || "0");
  const endTime = parseFloat(searchParams.get("endTime") || "0");
  const [videoDuration, setVideoDuration] = useState(0);

  const [videoSeeking, setVideoSeeking] = useState<boolean>(false);
  // Seed from initialHighlightCommentId so opening the overlay from an
  // echo bubble doesn't flash an unhighlighted thread on first paint.
  const [activeCommentId, setActiveCommentId] = useState<string | null>(initialHighlightCommentId ?? null);
  const [activeMomentId, setActiveMomentId] = useState<string | null>(null);
  const { selectedMedia, setSelectedMedia, selectedTab, currentTime, playerRef, clearFilters, isVideoDetailsPage } =
    useVideoSessionData();

  // signedUploadUrl is only populated for session-tied media (the
  // resolver returns null when there's no VideoSession). Fall back to
  // mediaAttachmentUrl so chat-uploaded media renders in overlay mode.
  const videoUrl = selectedMedia?.signedUploadUrl || selectedMedia?.mediaAttachmentUrl || "";

  const handleDurationChange = (duration: number) => {
    setVideoDuration(duration);
  };

  useEffect(() => {
    return () => {
      setSelectedMedia(null); // cleanup runs on unmount
    };
  }, []);

  // When the overlay is opened from an echo-bubble click, seek the player
  // to the comment's timestamp and highlight the comment in the thread.
  // We watch videoDuration so the effect fires once the player knows how
  // long the video is (i.e. after metadata has loaded).
  useEffect(() => {
    if (!initialSeekTime || !playerRef.current || !videoUrl || videoDuration <= 0) return;
    playerRef.current.seekTo(initialSeekTime);
  }, [videoDuration, videoUrl, initialSeekTime]);

  useEffect(() => {
    if (location.state) {
      if (location.state.commentId) {
        setActiveCommentId(location.state.commentId);
      }
      if (location.state.momentId) {
        setActiveMomentId(location.state.momentId);
      }
      if (playerRef.current && videoUrl && videoDuration > 0) {
        if (location.state?.timestamp) {
          playerRef.current?.seekTo(location.state.timestamp);
        }
        if (location.state?.startTime && location.state?.endTime) {
          playerRef.current?.playInRange(location.state?.startTime, location.state?.endTime);
        }
      }
    }
  }, [location.state, playerRef, videoUrl, videoDuration]);

  if (!selectedMedia) return null;
  // Routed mode has always required a VideoSession. Chat-uploaded media
  // has none and is only rendered via the overlay mount.
  if (mode === VideoSessionMode.Routed && !selectedMedia.videoSession) return null;

  const handleTimeClick = (time: number) => {
    if (playerRef.current?.seekTo) {
      playerRef.current.seekTo(time);
    }
  };

  const handleMarkerClick = (timestamp: number, commentId: string) => {
    handleTimeClick(timestamp);
    setActiveCommentId(commentId);
  };
  const handleMomentMarkerClick = (startTime: number, endTime: number, momentId: string) => {
    if (playerRef.current) {
      playerRef.current.playInRange(startTime, endTime);
    }
    setActiveMomentId(momentId);
  };

  // Session-derived header data; only meaningful when the media has a
  // VideoSession (i.e. routed mode or overlay mounted on a session media).
  const session = selectedMedia.videoSession;
  const sessionLearnerName = session?.sessionlearnerSet?.edges[0]?.node?.sessionLearners?.learnermetadata;
  const sessionLearnerFullName = sessionLearnerName
    ? `${sessionLearnerName.firstName} ${sessionLearnerName.lastName}`
    : "";
  const providerMeta = session?.sessionproviderSet?.edges[0]?.node?.provider?.providermetadata;
  const caregiverMeta = session?.sessioncaregiverSet?.edges[0]?.node?.caregiver?.caregivermetadata;
  const formattedDate = session?.createdDate ? displayDate(videoDuration, new Date(session.createdDate)) : "";
  const sessionRecorder = providerMeta || caregiverMeta;

  // headerOverride wins (chat mode passes it — there's no VideoSession to
  // derive learner info from).
  const effectiveLearnerFullName = headerOverride?.learnerFullName ?? sessionLearnerFullName;

  const handleClose = () => {
    setSelectedMedia(null);
    clearFilters();
    if (onClose) {
      onClose();
      return;
    }
    navigate("/video-sessions");
  };

  // If availableTabs is set and the current selectedTab falls outside it
  // (e.g. VideoContainer.handleMomentMarkerClick flips it to Moments while
  // overlay mode only allows Activity), fall back to the first allowed
  // tab so the panel never goes blank. This is a render-time guard rather
  // than a useEffect because availableTabs only constrains the inline
  // overlay mount — VideoSideBar is hidden when userTabs.length <= 1, so
  // there's no other consumer that would observe the discrepancy.
  const effectiveSelectedTab =
    availableTabs && availableTabs.length > 0 && !availableTabs.includes(selectedTab) ? availableTabs[0] : selectedTab;
  const showTab = (tab: VideoSessionTab) =>
    effectiveSelectedTab === tab && (!availableTabs || availableTabs.includes(tab));

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <div className="border-b border-gray-200 shrink-0">
        <div className="px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex lg:items-center items-start">
            <button
              onClick={handleClose}
              className="mr-4 p-2 pt-1 lg:pt-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <HeaderContent
              learnerFullName={effectiveLearnerFullName}
              providerLastName={sessionRecorder?.lastName ?? ""}
              providerFirstName={sessionRecorder?.firstName ?? ""}
              date={headerOverride?.caption ?? formattedDate}
              sessionRating={session?.sessionRating ?? undefined}
              userType={headerOverride ? "Provider" : caregiverMeta ? "Caregiver" : "Provider"}
              hideAttendingRow={!!headerOverride}
            />
          </div>
        </div>
      </div>

      <div
        className={`flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-x-0 border-t border-limestone-200 overflow-y-auto ${isVideoDetailsPage ? "max-lg:min-h-[calc(100vh-18rem)] lg:overflow-y-auto lg:h-[calc(100vh-10rem)]" : ""}`}
      >
        <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-2 bg-limestone-50 flex flex-col lg:overflow-y-auto lg:py-4 lg:px-4">
          <div className="w-full lg:max-w-5xl lg:mx-auto lg:rounded-2xl bg-transparent lg:pb-8 lg:pt-4 lg:px-6 px-0 flex flex-col">
            <div className="w-full space-y-4 lg:space-y-4">
              <div className={`relative w-full `}>
                <div className="relative" data-video-player>
                  {videoUrl && (
                    <VideoContainer
                      videoUrl={videoUrl}
                      onDurationChange={handleDurationChange}
                      onSeeking={() => setVideoSeeking(true)}
                      onSeeked={() => setVideoSeeking(false)}
                      onMarkerClick={handleMarkerClick}
                      onTimelineClick={handleTimeClick}
                      onMomentMarkerClick={handleMomentMarkerClick}
                      enabledFocus={false}
                      externalPlayerRef={playerRef}
                      data-video-container
                      onPlayerReady={() => {
                        if (startTime && endTime) {
                          playerRef.current?.seekTo(startTime);
                          playerRef.current?.playInRange(startTime, endTime);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-1 bg-white lg:overflow-y-auto lg:px-0 px-4">
          <VideoSideBar currentTime={currentTime} availableTabs={availableTabs} />
          {showTab(VideoSessionTab.Activity) ? (
            <VideoComments
              sessionNote={session?.comment ?? undefined}
              sessionRecorder={`${sessionRecorder?.firstName ?? "N/A"} ${sessionRecorder?.lastName ?? ""}`}
              onClick={handleTimeClick}
              activeCommentId={activeCommentId ?? ""}
              pinnedNote={pinnedNote}
            />
          ) : showTab(VideoSessionTab.Transcript) ? (
            <VideoTranscript currentTime={currentTime} onClick={(time) => playerRef.current?.seekTo(time)} />
          ) : showTab(VideoSessionTab.Moments) ? (
            <VideoMoments
              onClick={handleMomentMarkerClick}
              videoSeeking={videoSeeking}
              media={selectedMedia}
              activeMomentId={activeMomentId ?? ""}
            />
          ) : showTab(VideoSessionTab.Summary) ? (
            <VideoSummary
              onTimeStampClick={handleTimeClick}
              onDurationClick={(startTime, endTime) => {
                playerRef.current?.playInRange(startTime, endTime);
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
